/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Else, If, Then } from "react-if";
import { CSSObject } from "styled-components";
import {
  Box,
  Button,
  FlexContainer,
  Heading,
  ModalConfirm,
  Tag,
  Text,
  theme,
} from "@nordcloud/gnui";
import { DiscoveryRule } from "~/generated/graphql";
import { DiscoveryRulesForm, LoaderWrap } from "~/components";
import { SUCCESS_TEXT } from "~/constants";
import { useDiscoveryRules } from "~/hooks";
import { useRemoveEnvironment } from "~/hooks/graphql";
import { showError, showSuccess } from "~/services/toast";
import {
  generateActionConfirmText,
  handleError,
  inflect,
  isNotEmpty,
  toInputError,
} from "~/tools";
import { EnvironmentInput } from "~/views/environments/EnvironmentInput";
import { EditEnvironmentSidebar } from "./EnvironmentSidebars";
import { Env } from "./types";

type EnvironmentData = {
  environment: Env;
};

const FlexCss: CSSObject = {
  padding: `0 ${theme.spacing.spacing02}`,
  flexDirection: "column",
  alignItems: "flex-start",
  borderRadius: 0,
  alignSelf: "stretch",
};

export function EnvironmentItem({ environment }: EnvironmentData) {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);

  const { loading, error, rules, update } = useDiscoveryRules({
    envId: environment.nid,
    onSuccess: () => {
      showSuccess(SUCCESS_TEXT.discoveryRulesChanged);
    },
  });

  const [removeEnvironment] = useRemoveEnvironment();

  const onRemoveEnvironment = async (nid: string) => {
    try {
      await removeEnvironment({ nid });

      setIsModalConfirmOpen(false);
      showSuccess(SUCCESS_TEXT.environmentRemoved);
    } catch (err) {
      handleError(toInputError(err));
    }
  };

  const handleSubmit = (newRules: DiscoveryRule[]) => {
    setShowForm(false);
    update(newRules);
  };

  React.useEffect(() => {
    if (isNotEmpty(error)) {
      showError(error);
    }
  }, [error]);

  const numOfRules = rules.length ?? 0;

  return (
    <Box boxStyle="lightGrey" spacing="spacing02" innerSpacing="spacing03">
      <FlexContainer alignItems="flex-start">
        <FlexContainer css={FlexCss}>
          <Heading level={3} mb={theme.spacing.spacing01}>
            {environment.name}
          </Heading>
          <Text size="sm" tag="div">
            {environment.environmentType}
          </Text>
        </FlexContainer>
        <FlexContainer
          css={Object.assign(
            {
              borderLeft: `1px solid ${theme.color.border.border01}`,
            },
            FlexCss
          )}
        >
          <Text size="sm" tag="div" mb={theme.spacing.spacing02}>
            Environment belongs to Organizational Units:
          </Text>
          <EnvironmentInput environmentId={environment.nid} />
        </FlexContainer>
        <LoaderWrap inContent loading={loading}>
          <FlexContainer
            css={Object.assign(
              {
                borderLeft: `1px solid ${theme.color.border.border01}`,
              },
              FlexCss
            )}
          >
            <Text size="sm" tag="div" mb={theme.spacing.spacing02}>
              Discovery Rules:
            </Text>
            <If condition={numOfRules > 0}>
              <Then>
                <Button severity="low" onClick={() => setShowForm(true)}>
                  Added ({numOfRules}) {inflect("Discovery Rule")(numOfRules)}
                </Button>
              </Then>
              <Else>
                <Tag
                  text="Setup Discovery Rules"
                  icon="edit"
                  onClick={() => setShowForm(true)}
                />
              </Else>
            </If>
          </FlexContainer>
        </LoaderWrap>
        <FlexContainer
          css={Object.assign(
            {
              flex: 1,
              borderLeft: `1px solid ${theme.color.border.border01}`,
            },
            FlexCss
          )}
        >
          <EditEnvironmentSidebar environment={environment} />
          <Button
            icon="trash"
            size="md"
            margin={theme.spacing.spacing01}
            severity="low"
            onClick={() => setIsModalConfirmOpen(true)}
          />
        </FlexContainer>
      </FlexContainer>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onRemoveEnvironment(environment.nid)}
        actionLabel="Delete"
        contentLabel="Delete Environment"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(environment.name)("delete")()}
      </ModalConfirm>
      <DiscoveryRulesForm
        isOpen={showForm}
        rules={rules}
        onSubmit={handleSubmit}
        onClose={() => setShowForm(false)}
      />
    </Box>
  );
}
